/**
 * 퀘스트 스팟 전체화면 안내판 — 스캔 결과를 아이가 읽을 수 있게 크게 알린다.
 *
 * 디자인 출처 (Figma ZOOLUNG-ZOOLUNG-WEBAPP-PROJECT) — ⚠️ 노드 번호는 재활용된다.
 * `2937:39753` 은 원래 "이미 완료" 였다가 2026-08-05 "유효하지 않은 QR" 로 바뀌었다.
 * **노드 번호만 보고 어느 안내판인지 판단하지 말고 프레임 내용을 확인할 것.**
 *   · 이미 완료         node 3172:40219 — 정글 배경
 *   · 등록되지 않음     node 2936:39627 — 단풍 배경
 *   · 유효하지 않은 QR  node 2937:39753 — 수중 배경
 *   · 첫 미션 먼저      디자인 미확정 — 기존 숲 배경(`done_bg_*.webp`) 유지
 *
 * 네 프레임 모두 **간판 안쪽 좌표·타이포가 동일**하고 배경 그림과 문구만 다르다
 * (제목 128px top321 / 본문 64px top661 / #005400 / 본문 1행만 #FF5816).
 * 그래서 화면마다 컴포넌트를 만들지 않고 문구·배경만 상수로 갈아 끼운다 — 따로 만들면
 * 한쪽 디자인 수정이 다른 쪽에 반영되지 않는다.
 *
 * 폰트는 tools 에 이미 있는 브랜드 폰트(`assets/fonts/andong-katuri/Katuri.otf`)를 쓴다.
 */
import React from 'react';
import './MissionNoticeBoard.css';

/**
 * 이미 완료한 미션 (Figma 3172:40219).
 * 🔴 적용 범위는 `already_tagged` **전부**(전진 완료 여부 무관) — 사용자 결정 2026-07-30.
 *    그래서 모든 미션을 끝낸 아이에게도 "다른 미션 스팟을 찾아" 로 안내된다(공시된 트레이드오프).
 */
export const NOTICE_ALREADY_DONE = Object.freeze({
  key: 'already-done',
  title: Object.freeze(['이미 완료한', '미션이에요!']),
  body: Object.freeze([
    '이 미션은 이미 완료했어요.',
    '다른 미션 스팟을 찾아',
    '새로운 미션에 도전해 보세요!',
  ]),
  // 🔴 2026-08-05 — 이 안내판만 새 디자인(Figma 3172:40219)으로 교체했다.
  //    정글 배경 + 나무 간판 + 로고 구성이라 나머지 3종(숲 배경)과 그림·좌표가 다르다.
  //    **문구는 그대로다** — 순수 비주얼 교체다.
  //    ⚠️ 다른 안내판에 이 테마를 복사해 붙이지 마라. 배경이 이 문구 분량(제목 2행/본문 3행)에
  //       맞춰 그려져 있어 줄 수가 다르면 간판을 넘친다.
  theme: 'board',
  // 본문 중 강조(주황 #FF5816)할 줄 인덱스. 나머지는 기본색(#005400).
  // CSS `:first-child` 로 처리하지 않는 이유 — "왜 첫 줄만 다른가"가 코드에 남지 않는다.
  accentBody: Object.freeze([0]),
});

/**
 * 등록되지 않은 QR (Figma 2936:39627).
 * 대상: 앱 미등록 밴드(`BAND_UNASSIGNED`) · DB 미등록·위조(`UNKNOWN_BAND`) · 형식 오류
 * (`INVALID_BAND`) · **파싱 실패**(타사 QR·상품 바코드·구형 `JRB_` — 서버에 도달조차 못 함).
 */
export const NOTICE_UNREGISTERED = Object.freeze({
  key: 'unregistered',
  title: Object.freeze(['등록되지 않은', '주렁밴드예요']),
  body: Object.freeze([
    // 🔴 2026-08-05 문구 변경 — 디자인 갱신(Figma 2936:39627)에 맞췄다.
    //    이전: '주렁밴드가 앱과 연결되지 않았어요.'
    '밴드가 등록되지 않았어요.',
    '앱에서 밴드를 등록하면',
    '모든 미션에 참여할 수 있어요.',
  ]),
  // 이미완료와 **같은 간판 레이아웃**, 배경만 단풍(`mnb--unregistered`)이다.
  theme: 'board',
  accentBody: Object.freeze([0]),
});

/**
 * 사용할 수 없는 QR — **회수된 밴드**(사유 무관) + **탈퇴 회원의 회원 QR** (Figma 2937:39753).
 *
 * 위 `NOTICE_UNREGISTERED` 와 **정반대 안내**라 절대 합치면 안 된다:
 *   · 미등록(`available`)   → "앱에서 등록하면 참여 가능"  ← 이 QR 은 **살아 있다**
 *   · 회수/탈퇴(`revoked`)  → "이 QR 은 못 쓴다, 새 밴드"  ← 이 QR 은 **죽었다(종착 상태)**
 *
 * 회수 사유를 가리지 않는 것은 사용자 확정(2026-08-04)이다 — `revoked` 는 종착 상태라
 * 분실·탈퇴·자녀삭제·배치폐기 어느 쪽이든 결론이 "새 밴드가 필요하다"로 같다.
 * 그 덕에 서버가 사유를 실어 보낼 필요가 없다.
 */
export const NOTICE_INVALID_QR = Object.freeze({
  key: 'invalid-qr',
  // 🔴 2026-08-05 문구 변경 — 디자인 갱신(Figma 2937:39753)에 맞췄다.
  //    이전: ['유효하지 않은 QR입니다'] (1행, 마침표 없음)
  title: Object.freeze(['유효하지 않은', 'QR입니다.']),
  body: Object.freeze([
    '이 QR은 사용할 수 없습니다.',
    // 🔴 디자인 원문은 '새로운밴드'(붙임)지만 **의도적으로 띄어 쓴다** — 한국어 맞춤법상
    //    '새로운 밴드'가 맞고, 아이가 읽는 화면에 오탈자를 내보낼 수 없다. 가운데 정렬이라
    //    레이아웃에도 영향이 없다. 디자인 원문 그대로 가기로 정해지면 이 줄만 붙이면 된다.
    '앱에서 새로운 밴드를 등록하여',
    // 이전: '미션을 시작해 보세요'
    '미션을 시작해 주세요',
  ]),
  // 이미완료·미등록과 **같은 간판 레이아웃**, 배경만 수중(`mnb--invalid-qr`)이다.
  theme: 'board',
  accentBody: Object.freeze([0]),
});

/**
 * 회원 QR 자체가 유효하지 않을 때 — **서명 불일치 / 미가입 회원** (서버 `INVALID_MEMBER_QR`).
 *
 * 🔴 위 `NOTICE_INVALID_QR`(밴드가 죽은 경우)과 **원인이 다르다**:
 *   · 회수된 밴드·탈퇴 회원 → "새 밴드가 필요하다"        ← 밴드 문제
 *   · 서명 불일치·미가입   → "가입부터 해야 한다"          ← **가입** 문제
 *   그래서 본문이 밴드 등록이 아니라 회원가입을 안내한다. 밴드 안내를 내보내면 틀린 안내다.
 *
 * 🔴 **디자인(Figma)이 없다.** 사용자 확정(2026-08-19)으로 `invalid-qr` 안내판을 그대로
 *    재사용하고 문구만 바꾼다 — 이 경우만 평문 메시지로 떨어져 4종과 화면이 갈렸기 때문이다.
 *    배경은 `invalid-qr` 과 **같은 자산**을 쓴다(CSS `.mnb--invalid-member-qr`).
 *    나중에 전용 디자인이 나오면 그 규칙의 배경만 갈아끼우면 된다.
 */
export const NOTICE_INVALID_MEMBER_QR = Object.freeze({
  key: 'invalid-member-qr',
  title: Object.freeze(['유효하지 않은', '회원 QR입니다.']),
  body: Object.freeze([
    '이 QR은 사용할 수 없습니다.',
    '앱에서 회원가입 후',
    '미션을 시작해 주세요',
  ]),
  theme: 'board',
  accentBody: Object.freeze([0]),
});

/**
 * 첫 미션을 끝내기 전에 다른 미션을 진행하려 했을 때 (서버 `QUEST_FIRST_MISSION_REQUIRED`).
 *
 * 🔴 제목은 **사용자 지정 원문 그대로**다(2026-08-04). 문구를 다듬지 마라 — 테스트가 고정한다.
 * 본문 3줄은 작성자가 제안한 보조 문구이며 교체해도 무방하다(사용자 미지정).
 *
 * `displayMs` 를 스스로 싣는다 — 이 안내판만 **5초**(사용자 지정)이고 나머지 3종은 4초다.
 * 상수를 하나 더 만들어 분기하면 표시시간 판정이 둘로 갈라지므로, 안내판이 자기 시간을
 * 들고 있고 `feedbackDisplayMs` 가 한 곳에서 읽는다.
 */
export const NOTICE_FIRST_MISSION_FIRST = Object.freeze({
  key: 'first-mission-first',
  // 🔴 2026-08-19 — Figma `3172:40232` **현재 내용**으로 재교체(사용자 지시).
  //    2026-08-06 반영 당시 같은 노드에 자리표시자 `ㅇㅇㅇㅇㅇ`·`ㅇㅇㅇ` 가 들어 있어
  //    그것을 확정 문구로 확인받아 넣었는데, 이후 디자이너가 실제 문구로 바꿨다.
  //    노드 번호가 그대로라 코드는 따라가지 않았고 테스트가 옛 값을 고정하고 있어
  //    **테스트는 통과하는데 화면만 디자인과 다른** 상태가 유지됐다.
  //    ⚠️ 디자인이 자리표시자처럼 보이면 확정 문구로 굳히기 전에 노드를 다시 확인할 것.
  //    서버 예외 메시지(`quest_service.py:153`)는 이 안내판 경로에서 화면에 노출되지
  //    않으므로 그대로 둔다.
  title: Object.freeze(['베이스캠프로', '이동해 주세요!']),
  body: Object.freeze([
    '아직 미션에 참가하지 않았습니다.',
    '캠프로 고고~!',
    '먼저 캠프로 가서 미션을 시작해주세요.',
  ]),
  // 나머지 3종과 같은 간판 레이아웃, 배경만 정글(mission_etc003).
  theme: 'board',
  accentBody: Object.freeze([0]),
  displayMs: 5000,
});

/* ===========================================================================
 * 여권 QR 안내판 4종 + 파싱실패 1종 (2026-09-01)
 * 권위: docs/2026-09-01-passport-qr-mission-design.md §4.8 (2)·(4)
 *
 * 🔴 **기존 안내판을 재사용하지 않는 이유가 이 기능의 출발점이다.**
 *    `NOTICE_UNREGISTERED` 는 "앱에서 **밴드**를 등록하면"이라고 말한다. 여권을 비춘
 *    아이에게 이 안내가 뜨면 **아무리 시켜도 해결되지 않는 지시**를 하는 셈이다(§1.1).
 *    그렇다고 기존 문구를 겸용으로 고치면 문자열을 고정한 기존 테스트가 깨진다 →
 *    **신설**하고 기존 문구는 한 글자도 건드리지 않는다.
 *
 * 🔴 **`theme: 'board'` 가 빠지면 ①②③이 전부 죽는다.** `themeClass` 가 빈 문자열이 되어
 *    (`:170`) `mnb--{key}` 가 아예 안 붙고, 배경은 기본 `done_bg`·폰트는 Katuri 로
 *    **조용히** 어긋난다. 신설 시 4가지가 한 세트다:
 *      ④ 이 상수의 `theme` · ① `.mnb--{key}` 배경 · ② 1600px 규칙 · ③ `NOTICE_BACKGROUNDS`
 *
 * 배경 자산은 **새로 만들지 않는다** — 선례(`invalid-member-qr`, 2026-08-19)를 따라
 * "등록하면 해결" 계열은 단풍(`unregistered`), "이 QR 은 못 쓴다" 계열은 수중(`invalid-qr`)을
 * 공유한다. 전용 디자인이 나오면 CSS 두 줄과 프리로더 한 줄만 갈아끼우면 된다.
 *
 * 레이아웃은 기존 간판과 같으므로 **제목 2행 / 본문 3행**을 지킨다 — 줄 수가 다르면 간판을 넘친다.
 * =========================================================================== */

/**
 * 원장이 `available` — 스티커는 고객 손에 있으나 앱 등록 전 (`PASSPORT_QR_UNREGISTERED`).
 * 밴드의 `BAND_UNASSIGNED` 와 같은 "등록하면 해결" 계열이라 단풍 배경을 공유한다.
 */
export const NOTICE_PASSPORT_UNREGISTERED = Object.freeze({
  key: 'passport-unregistered',
  title: Object.freeze(['등록되지 않은', '여권이에요']),
  body: Object.freeze([
    '여권이 등록되지 않았어요.',
    '앱에서 여권을 등록하면',
    '모든 미션에 참여할 수 있어요.',
  ]),
  theme: 'board',
  accentBody: Object.freeze([0]),
});

/**
 * 만료 여권 (`PASSPORT_EXPIRED`) — 사용자 확정 D-1.
 * 🔴 아래 `NOTICE_PASSPORT_CANCELLED` 와 **합치지 않는다**: 만료는 시간이 지나서이고
 *    취소는 사람이 되돌린 것이라, 고객에게 설명할 내용도 현장 대처도 다르다.
 *    tools 여권취소 화면에서도 같은 이유로 두 사유를 갈랐다(§4.8 In 13).
 */
export const NOTICE_PASSPORT_EXPIRED = Object.freeze({
  key: 'passport-expired',
  title: Object.freeze(['기간이 끝난', '여권이에요']),
  body: Object.freeze([
    '이 여권은 사용 기간이 끝났어요.',
    '새 여권으로 다시 오면',
    '미션에 참여할 수 있어요.',
  ]),
  theme: 'board',
  accentBody: Object.freeze([0]),
});

/** 취소된 여권 (`PASSPORT_CANCELLED`) — 사용자 확정 D-1. */
export const NOTICE_PASSPORT_CANCELLED = Object.freeze({
  key: 'passport-cancelled',
  title: Object.freeze(['취소된', '여권이에요']),
  body: Object.freeze([
    '이 여권은 취소되었어요.',
    '자세한 내용은 매장 직원에게',
    '문의해 주세요.',
  ]),
  theme: 'board',
  accentBody: Object.freeze([0]),
});

/**
 * 사용할 수 없는 여권 QR — `PASSPORT_QR_VOID` · `PASSPORT_QR_NOT_FOUND` ·
 * `INVALID_PASSPORT_QR` · `PASSPORT_NOT_FOUND` **4코드 공용**.
 *
 * 🔴 "실패 사유를 뭉치지 않는다"(§4.1 원칙 4)는 **error_code 와 서버 message 를 분리한다**는
 *    뜻이다. 이 네 코드는 **현장 대처가 완전히 같아서**(재스캔 후에도 안 되면 매장 문의)
 *    안내판만 병합한다. 원인 구분이 필요한 운영자는 `passport_qr_usages`·서버 로그에서
 *    error_code 로 확인한다.
 *
 * ⚠️ 밴드는 원장 미등재(`UNKNOWN_BAND`)를 "등록하면 해결" 계열로 두는데 여권은
 *    `PASSPORT_QR_NOT_FOUND` 를 여기(사용 불가)에 둔다. **비대칭이 의도적이다** —
 *    밴드는 TOFU 라 미등재가 정상일 수 있지만, 여권 코드는 CRM 이 전량 발행하므로
 *    (`batch_id NOT NULL`) 미등재는 위조·오출력이며 등록으로 해결되지 않는다.
 */
export const NOTICE_PASSPORT_UNUSABLE = Object.freeze({
  key: 'passport-unusable',
  title: Object.freeze(['사용할 수 없는', '여권 QR이에요']),
  body: Object.freeze([
    '이 QR은 사용할 수 없습니다.',
    '다시 비춰도 안 되면',
    '매장 직원에게 문의해 주세요.',
  ]),
  theme: 'board',
  accentBody: Object.freeze([0]),
});

/**
 * 파싱 실패 — 어느 식별자도 아닌 스캔값(타사 QR·상품 바코드·구형 `JRB_` 밴드).
 *
 * 🔴 이전에는 `NOTICE_UNREGISTERED`("등록되지 않은 **주렁밴드**예요")를 재사용했는데,
 *    여권 QR 을 비춘 아이에게도 그 안내가 떴다 — 그것이 §1.1 이 문제 삼은 오안내다.
 *    이제 여권은 서버까지 도달하므로 이 안내판은 **정말로 못 읽은 경우** 전용이 되고,
 *    문구도 "무엇을 비춰야 하는지"를 말한다.
 */
export const NOTICE_UNPARSABLE = Object.freeze({
  key: 'unparsable',
  title: Object.freeze(['QR을 읽지', '못했어요']),
  body: Object.freeze([
    '회원 QR을 다시 비춰주세요.',
    '주렁밴드나 여권 QR도',
    '미션에 사용할 수 있어요.',
  ]),
  theme: 'board',
  accentBody: Object.freeze([0]),
});

/**
 * @param {object} props
 * @param {{key:string,title:string[],body:string[]}} props.notice 표시할 문구 세트
 * @param {string} [props.rewardMsg] 있을 때만 덧붙이는 보상 문구
 *   🔴 이미 참여한 스팟이라도 **복구 지급**(spot_reward.granted=true)이 일어날 수 있다.
 *      고정 문구로 갈아치우면서 이 정보를 흘리면 아이가 보상받은 걸 모른다.
 */
export default function MissionNoticeBoard({ notice, rewardMsg }) {
  if (!notice) return null;
  // formatSpotRewardMsg 는 ' · 주렁주화 3개 적립!' 처럼 구분자로 시작한다 — 단독 줄로 쓸 때 벗긴다.
  const reward = typeof rewardMsg === 'string' ? rewardMsg.replace(/^\s*·\s*/, '').trim() : '';

  // 테마는 안내판마다 다른 배경·좌표를 고르는 스위치다(현재 'board' 만 존재).
  // 값이 없으면 클래스가 붙지 않아 **기존 3종은 렌더 결과가 한 글자도 바뀌지 않는다.**
  // 테마는 **레이아웃**(간판 안쪽 좌표·글자 크기)을, key 클래스는 **배경 그림**을 고른다.
  // 둘을 한 클래스로 묶으면 배경이 다른 안내판마다 레이아웃 CSS 가 통째로 복제된다
  // (이미완료=정글 / 미등록=단풍 — 좌표는 같고 그림만 다르다).
  const themeClass = notice.theme ? ` mnb--${notice.theme} mnb--${notice.key}` : '';
  const accent = new Set(notice.accentBody || []);

  return (
    <div className={`mnb${themeClass}`} data-testid={`mission-notice-${notice.key}`}>
      <p className="mnb-title">
        {notice.title.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
        ))}
      </p>
      <p className="mnb-body">
        {notice.body.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {/* 강조 줄만 span 으로 감싼다 — 지정이 없으면 텍스트 노드 그대로라 기존과 동일 */}
            {accent.has(i) ? <span className="mnb-body-accent">{line}</span> : line}
          </React.Fragment>
        ))}
      </p>
      {reward && <p className="mnb-reward">{reward}</p>}
    </div>
  );
}
